import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { type HTMLAttributes, type ReactNode, type RefObject, useRef } from 'react';

interface ScoreModalShellProps {
  ariaLabel: string;
  canClose?: boolean;
  children: ReactNode;
  className?: string;
  onClose: () => void;
  restoreFocusRef?: RefObject<HTMLElement>;
}

export function ScoreModalShell({
  ariaLabel,
  canClose = true,
  children,
  className,
  onClose,
  restoreFocusRef,
}: ScoreModalShellProps) {
  const openerFocusRef = useRef<HTMLElement | null>(
    typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null)
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && canClose) onClose();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        aria-modal="true"
        showCloseButton={false}
        overlayClassName="bg-background/80 backdrop-blur-sm"
        onCloseAutoFocus={(event) => {
          const restoreTarget = restoreFocusRef?.current?.isConnected
            ? restoreFocusRef.current
            : openerFocusRef.current?.isConnected
              ? openerFocusRef.current
              : null;

          if (restoreTarget) {
            event.preventDefault();
            restoreTarget.focus();
          }
        }}
        className={cn(
          'fixed left-1/2 top-4 z-50 flex max-h-[calc(100dvh-2rem)] min-h-0 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 translate-y-0 flex-col overflow-hidden border-0 bg-transparent p-0 text-foreground shadow-none sm:top-1/2 sm:-translate-y-1/2 sm:rounded-none',
          className
        )}
      >
        <DialogTitle className="sr-only">{ariaLabel}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function ScoreModalBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-score-modal-body
      className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', className)}
      {...props}
    />
  );
}

export function ScoreModalFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-score-modal-footer
      className={cn(
        'shrink-0 border-t border-border bg-card px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
        className
      )}
      {...props}
    />
  );
}
